import React, { useState } from 'react';
import { FileText, Menu, X, ChevronDown, Flame, Zap } from 'lucide-react';

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileBrowseOpen, setMobileBrowseOpen] = useState(false);
  const [desktopBrowseOpen, setDesktopBrowseOpen] = useState(false);

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <div className="w-8 h-8 bg-gradient-to-br from-red-600 to-orange-500 rounded-lg flex items-center justify-center shadow-md">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div className="text-lg font-bold">
              <span className="text-red-700">Paperwork</span>
              <span className="text-gray-800">Depot</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-4 2xl:gap-6">
            <a
              href="/free-guide"
              className="text-orange-600 hover:text-orange-700 font-bold text-[11px] lg:text-[12px] xl:text-sm transition-colors whitespace-nowrap"
              data-testid="nav-free-guides-desktop"
            >
              Free Guides
            </a>
            <a
              href="/category/lawsuits"
              className="text-red-700 hover:text-red-800 font-bold text-[11px] lg:text-[12px] xl:text-sm transition-colors flex items-center gap-1 whitespace-nowrap"
              data-testid="nav-civil-lawsuit"
            >
              Civil Lawsuit Templates
              <Flame size={13} className="text-orange-500" />
              <span className="text-yellow-500 text-[9px] font-bold leading-none">NEW</span>
            </a>
            <a
              href="/#why-templates-work"
              className="text-gray-700 hover:text-red-700 font-medium text-[11px] lg:text-[12px] xl:text-sm transition-colors whitespace-nowrap"
              data-testid="nav-why-templates"
            >
              Why These Templates Work
            </a>
            <div
              className="relative"
              onMouseEnter={() => setDesktopBrowseOpen(true)}
              onMouseLeave={() => setDesktopBrowseOpen(false)}
            >
              <button
                className="text-gray-700 hover:text-red-700 font-medium text-[11px] lg:text-[12px] xl:text-sm transition-colors whitespace-nowrap flex items-center gap-1"
                data-testid="nav-browse-guides"
                onClick={(e) => { e.preventDefault(); setDesktopBrowseOpen(!desktopBrowseOpen); }}
              >
                Browse Guides
                <ChevronDown size={13} className={`transition-transform duration-200 ${desktopBrowseOpen ? 'rotate-180' : ''}`} />
              </button>
              {desktopBrowseOpen && (
                <div className="absolute top-full left-0 mt-0 pt-2 z-50" data-testid="desktop-browse-dropdown">
                  <div className="bg-white rounded-lg shadow-xl border border-gray-200 py-2 w-48">
                    <a href="/browse" className="block px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-red-700 font-medium transition-colors" data-testid="desktop-dd-all">All Guides</a>
                    <a href="/category/credit-disputes" className="block px-4 py-2 text-sm text-gray-600 hover:bg-orange-50 hover:text-red-700 transition-colors" data-testid="desktop-dd-credit">Credit Disputes</a>
                    <a href="/category/business-credit" className="block px-4 py-2 text-sm text-gray-600 hover:bg-orange-50 hover:text-red-700 transition-colors" data-testid="desktop-dd-business">Business Credit</a>
                    <a href="/category/court-motions" className="block px-4 py-2 text-sm text-gray-600 hover:bg-orange-50 hover:text-red-700 transition-colors" data-testid="desktop-dd-court">Court Motions</a>
                    <a href="/category/affidavits" className="block px-4 py-2 text-sm text-gray-600 hover:bg-orange-50 hover:text-red-700 transition-colors" data-testid="desktop-dd-affidavits">Affidavits</a>
                    <a href="/category/trust-estate" className="block px-4 py-2 text-sm text-gray-600 hover:bg-orange-50 hover:text-red-700 transition-colors" data-testid="desktop-dd-trust">Trust & Estate</a>
                    <a href="/category/legal-notices" className="block px-4 py-2 text-sm text-gray-600 hover:bg-orange-50 hover:text-red-700 transition-colors" data-testid="desktop-dd-legal">Legal Notices</a>
                    <a href="/category/landlord-tenant" className="block px-4 py-2 text-sm text-gray-600 hover:bg-orange-50 hover:text-red-700 transition-colors" data-testid="desktop-dd-landlord">Landlord-Tenant</a>
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <a href="/category/lawsuits" className="flex items-center gap-1 px-4 py-2 text-sm text-red-700 hover:bg-orange-50 font-semibold transition-colors" data-testid="desktop-dd-lawsuits">
                        Civil Lawsuits <Flame size={12} className="text-orange-500" />
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <a
              href="/trending"
              className="text-gray-700 hover:text-red-700 font-medium text-[11px] lg:text-[12px] xl:text-sm transition-colors whitespace-nowrap flex items-center gap-1"
              data-testid="nav-trending"
            >
              <Zap size={13} className="text-yellow-500" />
              Trending
            </a>
            <a
              href="/support"
              className="text-gray-700 hover:text-red-700 font-medium text-[11px] lg:text-[12px] xl:text-sm transition-colors whitespace-nowrap"
              data-testid="nav-support"
            >
              Support
            </a>
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center">
            <a
              href="/browse"
              className="bg-red-700 text-white hover:bg-red-800 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
              data-testid="nav-shop-now"
            >
              Shop Now
            </a>
          </div>

          {/* Mobile — Free Guides + Civil Lawsuit CTA + Hamburger */}
          <div className="flex lg:hidden items-center gap-3">
            <a
              href="/free-guide"
              className="text-orange-600 font-extrabold text-[10px] tracking-tight shrink-0"
              data-testid="mobile-free-guides-link"
            >
              Free Guides
            </a>
            <a
              href="/category/lawsuits"
              className="flex flex-col items-center leading-none shrink-0"
              data-testid="mobile-civil-lawsuit-link"
            >
              <span className="text-[10px] font-extrabold text-red-700 tracking-tight">🔥Civil</span>
              <span className="text-[10px] font-extrabold text-red-700 tracking-tight ml-0.5">Lawsuit</span>
              <span className="text-[10px] font-extrabold text-red-700 tracking-tight ml-1">Templates</span>
              <span className="text-[10px] font-extrabold text-yellow-500 tracking-tight">NEW</span>
            </a>
            <button
              className="text-gray-700 p-1"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            <a href="/category/lawsuits" className="flex items-center gap-2 text-red-700 font-bold py-2.5 text-sm border-b border-gray-100 mb-1">
              <Flame size={14} className="text-orange-500" />
              Civil Lawsuit Templates
              <span className="bg-emerald-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full leading-none">NEW</span>
            </a>
            <div>
              <button
                onClick={() => setMobileBrowseOpen(!mobileBrowseOpen)}
                className="w-full flex items-center justify-between text-gray-700 hover:text-red-700 font-medium py-2 text-sm"
                data-testid="mobile-browse-toggle"
              >
                Browse Guides
                <ChevronDown size={14} className={`transition-transform ${mobileBrowseOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileBrowseOpen && (
                <div className="ml-4 space-y-1 pb-1">
                  <a href="/browse" className="block text-gray-600 hover:text-red-700 py-1.5 text-sm">All</a>
                  <a href="/category/credit-disputes" className="block text-gray-600 hover:text-red-700 py-1.5 text-sm">Credit Disputes</a>
                  <a href="/category/business-credit" className="block text-gray-600 hover:text-red-700 py-1.5 text-sm">Business Credit</a>
                  <a href="/category/court-motions" className="block text-gray-600 hover:text-red-700 py-1.5 text-sm">Court Motions</a>
                  <a href="/category/affidavits" className="block text-gray-600 hover:text-red-700 py-1.5 text-sm">Affidavits</a>
                  <a href="/category/trust-estate" className="block text-gray-600 hover:text-red-700 py-1.5 text-sm">Trust & Estate</a>
                  <a href="/category/legal-notices" className="block text-gray-600 hover:text-red-700 py-1.5 text-sm">Legal Notices</a>
                  <a href="/category/landlord-tenant" className="block text-gray-600 hover:text-red-700 py-1.5 text-sm">Landlord-Tenant</a>
                  <a href="/category/lawsuits" className="block text-gray-600 hover:text-red-700 py-1.5 text-sm flex items-center gap-1">
                    Civil Lawsuits <Flame size={12} className="text-orange-500" />
                  </a>
                </div>
              )}
            </div>
            <a href="/bundles" className="block text-gray-700 hover:text-red-700 font-medium py-2 text-sm">Bundles</a>
            <a href="/trending" className="block text-gray-700 hover:text-red-700 font-medium py-2 text-sm flex items-center gap-1">
              <Zap size={13} className="text-yellow-500" /> Trending Templates
            </a>
            <a href="/free-guide" className="block text-gray-700 hover:text-red-700 font-medium py-2 text-sm">Free Guide</a>
            <a href="/support" className="block text-gray-700 hover:text-red-700 font-medium py-2 text-sm">Support</a>
            <div className="border-t border-gray-100 mt-2 pt-2">
              <a href="/terms" className="block text-gray-500 hover:text-red-700 py-1.5 text-xs">Terms of Use</a>
              <a href="/privacy" className="block text-gray-500 hover:text-red-700 py-1.5 text-xs">Privacy Policy</a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
